"use client";
import "./HistoryCard.css";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

/**
 * ✅ Convertit une date (ex: "Monday, 12 Aug 2025" ou ISO) -> { day: "12", mon: "AUG"/"أغس" }
 * - month est localisé selon la langue i18n (ar/fr/en)
 */
function getDayMonth(dateText, lang = "en") {
  const fallback = {
    day: "15",
    mon: lang?.startsWith("ar") ? "أغس" : "AUG",
  };

  if (!dateText) return fallback;

  // 1) Essayer parsing Date()
  const d = new Date(dateText);
  if (!isNaN(d)) {
    const day = String(d.getDate()).padStart(2, "0");

    // Mois localisé (court)
    let mon = d.toLocaleString(lang, { month: "short" });

    // Ajustements utiles pour l'arabe (parfois نقطة/مسافة)
    mon = String(mon).replace(/[.\s]/g, "").slice(0, 4);

    // EN => uppercase, AR => tel quel
    if (!lang.startsWith("ar")) mon = mon.toUpperCase();

    return { day, mon };
  }

  // 2) Fallback regex si format non parsable
  // ex: "15 Aug 2025" / "15 Août 2025" / "15 août 2025"
  const m = String(dateText).match(/\b(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\b/);
  if (m) {
    const day = m[1].padStart(2, "0");
    let mon = m[2].slice(0, lang.startsWith("ar") ? 4 : 3);
    if (!lang.startsWith("ar")) mon = mon.toUpperCase();
    return { day, mon };
  }

  return fallback;
}

// Normaliser la spécialité (comme dans ChatHeader et ContactsList)
const normalizeSpecialtyKey = (value) => {
  if (!value) return null;
  const norm = value.toString().trim().toLowerCase();
  if (!norm) return null;

  if (norm.includes("dermatolog")) return "dermatology";
  if (norm.includes("pediatr")) return "pediatrics";
  if (norm.includes("cardiolog")) return "cardiology";
  if (norm.includes("neurolog")) return "neurology";
  if (norm.includes("psychiatr")) return "psychiatry";
  if (norm.includes("ophthalmolog") || norm.includes("ophtalmolog")) return "ophthalmology";
  if (norm.includes("orthop")) return "orthopedics";
  if (norm.includes("gynecolog") || norm.includes("gynaecolog")) return "gynecology";
  if (norm.includes("dentist")) return "dentistry";
  if (norm.includes("general")) return "general";
  if (norm === "ent" || (norm.includes("ear") && norm.includes("nose") && norm.includes("throat"))) return "ent";
  return null;
};

const HistoryCard = ({ appointment = {}, onRebook, onMessage }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";

  const provider =
    appointment.providerDisplay || appointment.provider || t("appointmentss.defaults.doctorName", "Dr. Sarah Thompson");

  const rawSpecialty =
    appointment.providerTitle ||
    appointment.specialtyDisplay ||
    appointment.specialty ||
    t("appointmentss.defaults.specialty", "Pediatrician");

  // Traduire la spécialité comme dans ChatHeader
  const specialtyKey = useMemo(() => normalizeSpecialtyKey(rawSpecialty), [rawSpecialty]);
  const specialty = useMemo(() => {
    if (specialtyKey) {
      return t(`doctorFinder.specialties.${specialtyKey}`);
    }
    return rawSpecialty;
  }, [specialtyKey, rawSpecialty, t]);

  const dateText =
    appointment._display?.dateText ||
    appointment.date ||
    t("appointmentss.defaults.dateText", "Friday, 15 Aug 2025");

  const timeText =
    appointment._display?.timeText ||
    appointment.time ||
    t("appointmentss.defaults.timeText", "09:30 – 10:00 AM");

  const { day, mon } = getDayMonth(dateText, lang);

  const status = String(appointment.status || "missed").toLowerCase();
  const isMissed = status === "missed";

  const handleMessage = () => {
    if (typeof onMessage === "function") return onMessage(appointment);
  };

  const handleRebook = () => {
    if (typeof onRebook === "function") return onRebook(appointment);
  };

  return (
    <div className="history-card" dir={lang.startsWith("ar") ? "rtl" : "ltr"}>
      <div className="date-badge" aria-label={`${dateText} ${timeText}`}>
        <div className="date-day">{day}</div>
        <div className="date-mon">{mon}</div>
      </div>

      <div className="hc-info">
        <div className="hc-name" title={provider}>
         Dr. {provider}
        </div>
        <div className="hc-spec" title={specialty}>
          {specialty}
        </div>

        {/* ✅ optionnel: afficher date/heure si tu veux */}
        {/* <div className="hc-time" title={`${dateText} ${timeText}`}>{dateText} · {timeText}</div> */}
      </div>

      <div className={`hc-status ${isMissed ? "missed" : "default"}`}>
        {isMissed
          ? t("appointmentss.history.status.missed", "Missed Appointment")
          : t("appointmentss.history.status.attended", "Attended Appointment")}
      </div>

      <div className="hc-actions">
        <button className="hc-btn outline" onClick={handleMessage}>
          {t("appointmentss.history.actions.message", "MESSAGE")}
        </button>
        <button className="hc-btn primary" onClick={handleRebook}>
          {t("appointmentss.history.actions.rebook", "REBOOK")}
        </button>
      </div>
    </div>
  );
};

export default HistoryCard;
